import { Check } from "lucide-react";
import { FourArrows } from "assets/website";

export type SuiteTab = "toolkit" | "partner";

/**
 * Every talent enablement module is sold through one Selar storefront covering
 * all bundles, rather than the in-app store endpoint + Paystack checkout. Selar
 * owns pricing, so changes there don't need a release here.
 */
export const SELAR_STORE_URL = "https://selar.com/246i58f2m3";

export const openSelarStore = () => {
  window.open(SELAR_STORE_URL, "_blank", "noopener,noreferrer");
};

export const systemBadges: { label: string; bg: string }[] = [
  { label: "OKR Execution System", bg: "#605BFF" },
  { label: "Strategic Ownership Operating Framework™", bg: "#DB8400" },
  { label: "Performance Appraisal Kit", bg: "#11AF36" },
];

/** Small gradient-underlined section label (e.g. "Resources", "Solutions"). */
export const SectionPill: React.FC<{ label: string }> = ({ label }) => (
  <div className="inline-flex py-2 items-center gap-2 relative before:content-[''] before:absolute before:top-0 before:left-0 before:right-0 before:h-[1px] before:bg-[linear-gradient(90deg,#C6E0FF_0%,#000B55_100%)] after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[1px] after:bg-[linear-gradient(90deg,#C6E0FF_0%,#000B55_100%)]">
    <FourArrows />
    <span className="sm:text-lg text-base text-[#050B17] tracking-wider">
      {label}
    </span>
  </div>
);

/** Row of the three product badges (OKR / SOOF / Appraisal Kit). */
export const SystemBadges: React.FC<{ className?: string }> = ({ className }) => (
  <div
    className={`flex flex-wrap items-center justify-center gap-2 md:gap-3 ${
      className ?? ""
    }`}
  >
    {systemBadges.map((badge) => (
      <span
        key={badge.label}
        className="inline-flex items-center gap-1.5 rounded-full px-3 sm:py-1.5 py-1 sm:w-fit w-full sm:justify-start justify-center text-xs md:text-sm font-medium text-white"
        style={{ backgroundColor: badge.bg }}
      >
        <Check className="sm:w-3.5 w-3 aspect-square shrink-0 text-[#64CB5D]" strokeWidth={3} />
        {badge.label}
      </span>
    ))}
  </div>
);

/** Execution Toolkit / Work with a Partner segmented toggle. */
export const SuiteToggle: React.FC<{
  tab: SuiteTab;
  onChange: (tab: SuiteTab) => void;
}> = ({ tab, onChange }) => {
  const options: { key: SuiteTab; label: string }[] = [
    { key: "toolkit", label: "Execution Toolkit" },
    { key: "partner", label: "Work with a Partner" },
  ];

  return (
    <div className="inline-flex items-center rounded-full border-[2px] border-[#163B84] bg-white p-1.5">
      {options.map((option) => (
        <button
          key={option.key}
          onClick={() => onChange(option.key)}
          className={`rounded-full px-4 sm:px-6 py-2.5 text-sm md:text-base font-medium transition-colors duration-200 ${
            tab === option.key
              ? "bg-[#163B84] text-white"
              : "text-[#1D2939] hover:text-[#012169]"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};
